// src/hooks/usePushSubscription.js
// Manages web push subscription lifecycle for the current user.
import { useEffect, useRef, useState } from 'react';
import api from '../config/api';

const SW_PATH = '/sw.js';

function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const rawData = atob(base64);
  return Uint8Array.from([...rawData].map(c => c.charCodeAt(0)));
}

/**
 * Auto-subscribes the current user to push notifications if:
 * 1. Browser supports push
 * 2. User has already granted permission (or we prompt them)
 * 3. VAPID public key is configured on the backend
 *
 * Returns { supported, permission, subscribe, unsubscribe }
 */
export function usePushSubscription(autoPrompt = false) {
  const [permission, setPermission] = useState(
    () => ('Notification' in window ? Notification.permission : 'unsupported'),
  );
  const [supported] = useState(
    () => 'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window,
  );
  const registrationRef = useRef(null);

  // Register Service Worker once
  useEffect(() => {
    if (!supported) return;
    navigator.serviceWorker.register(SW_PATH).then(reg => {
      registrationRef.current = reg;
    }).catch(err => {
      console.warn('[push] SW register failed:', err);
    });
  }, [supported]);

  // Auto-subscribe when permission is already granted
  useEffect(() => {
    if (!supported) return;
    if (Notification.permission === 'granted') {
      _subscribeInternal();
    } else if (autoPrompt && Notification.permission === 'default') {
      _subscribeInternal();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [supported]);

  async function _subscribeInternal() {
    try {
      // Get VAPID key
      const { data } = await api.get('/api/push/vapid-public-key');
      if (!data?.key) return;

      // Wait for SW to be ready
      const reg = await navigator.serviceWorker.ready;
      registrationRef.current = reg;

      // Check existing subscription first
      let sub = await reg.pushManager.getSubscription();
      if (!sub) {
        // Request permission + subscribe
        const perm = await Notification.requestPermission();
        setPermission(perm);
        if (perm !== 'granted') return;

        sub = await reg.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey: urlBase64ToUint8Array(data.key),
        });
      }

      // Send subscription to backend
      await api.post('/api/push/subscribe', { subscription: sub.toJSON() });
    } catch (e) {
      // Silently ignore — push is a nice-to-have
      console.warn('[push] subscribe error:', e.message);
    }
  }

  async function subscribe() {
    await _subscribeInternal();
  }

  async function unsubscribe() {
    try {
      const reg = await navigator.serviceWorker.ready;
      const sub = await reg.pushManager.getSubscription();
      if (sub) {
        await api.delete('/api/push/unsubscribe', { data: { endpoint: sub.endpoint } });
        await sub.unsubscribe();
      }
      setPermission(Notification.permission); // reflect actual browser state, not assumed 'default'
    } catch (e) {
      console.warn('[push] unsubscribe error:', e.message);
    }
  }

  return { supported, permission, subscribe, unsubscribe };
}
